import {
    buildReportModalAspectRadarPoints,
    buildReportModalRatingBuckets,
    buildReportModalTopItems,
    buildReportModalTreemapItems,
    calculateReportModalWeightedAverageRating,
    sumReportModalMetric,
    type ReportModalBrandRow,
    type ReportModalProductRow,
} from '@/domain/reporting/reporting-modal-aggregations';
import {
    buildReportModalAverageRatingChartData,
    buildReportModalRatingDistributionChartData,
    type ReportModalAverageRatingChartData,
    type ReportModalRatingDistributionChartData,
    buildReportModalResponseTimeChartData,
    type ReportModalResponseTimeChartData,
} from '@/domain/reporting/reporting-modal-charts';
import { buildTreemapLayout } from '@/domain/reporting/reporting-shared.treemap-layout';
import {
    AibackReportAspectRowEntity,
    AibackReportPdfTopItemEntity,
    AibackReportPdfTreemapItemEntity,
    AibackReportRatingTimelinePointEntity,
    AibackReportResponseTimeTimelinePointEntity,
} from '@/entities/aiback-report.entity';

type ReportModalRatingBucket = {
    stars: number;
    value: number;
};

export type ReportModalBindingsInput = {
    isShown: boolean;
    isSummaryLoaded: boolean;
    preparedProducts: ReportModalProductRow[];
    preparedBrands: ReportModalBrandRow[];
    averageRatingTimeline: AibackReportRatingTimelinePointEntity[];
    averageResponseTimeTimeline: AibackReportResponseTimeTimelinePointEntity[];
    aspectRows: AibackReportAspectRowEntity[];
    periodAverageRating: number | null;
    periodTotalReviews: number;
    periodTotalAnswers: number;
    periodTotalAiAnalyses: number;
    periodChannelsCount: number;
    periodProductsCount: number;
    periodBrandsCount: number;
    periodTopChannels: AibackReportPdfTopItemEntity[];
    periodTopProducts: AibackReportPdfTopItemEntity[];
    periodTopBrands: AibackReportPdfTopItemEntity[];
    periodRatingBuckets: ReportModalRatingBucket[];
    periodTreemapItems: AibackReportPdfTreemapItemEntity[];
};

export type ReportModalBindings = {
    totalReviews: number;
    totalAnswers: number;
    answersShare: number | null;
    totalAiAnalyses: number;
    channelsCount: number;
    productsCount: number;
    brandsCount: number;
    companyAverageRating: number | null;
    topChannels: AibackReportPdfTopItemEntity[];
    topProducts: AibackReportPdfTopItemEntity[];
    topBrands: AibackReportPdfTopItemEntity[];
    ratingBuckets: ReportModalRatingBucket[];
    ratingDistributionChartData: ReportModalRatingDistributionChartData | null;
    averageRatingChartData: ReportModalAverageRatingChartData | null;
    responseTimeChartData: ReportModalResponseTimeChartData | null;
    treemapItems: AibackReportPdfTreemapItemEntity[];
    treemapLayout: ReturnType<typeof buildTreemapLayout>;
    aspectRadarPoints: ReturnType<typeof buildReportModalAspectRadarPoints>;
};

const calculateAnswersShare = (totalAnswers: number, totalReviews: number): number | null => {
    if (!totalReviews) {
        return null;
    }

    return (totalAnswers / totalReviews) * 100;
};

export const buildReportModalBindings = (input: ReportModalBindingsInput): ReportModalBindings => {
    const fallbackTotalReviews = sumReportModalMetric(input.preparedProducts, (product) => product.reviewCount);
    const fallbackTotalAnswers = sumReportModalMetric(input.preparedProducts, (product) => product.answerCount);
    const fallbackTotalAiAnalyses = sumReportModalMetric(input.preparedProducts, (product) => product.aiAnalysisCount);
    const fallbackAverageRating = calculateReportModalWeightedAverageRating(input.preparedProducts);
    const fallbackTopProducts = buildReportModalTopItems(input.preparedProducts);
    const fallbackTopBrands = buildReportModalTopItems(input.preparedBrands);
    const fallbackRatingBuckets = buildReportModalRatingBuckets(input.preparedProducts);
    const fallbackTreemapItems = buildReportModalTreemapItems(input.preparedProducts);

    const usePeriodValues = input.isShown || input.isSummaryLoaded;
    const totalReviews = usePeriodValues ? input.periodTotalReviews : fallbackTotalReviews;
    const totalAnswers = usePeriodValues ? input.periodTotalAnswers : fallbackTotalAnswers;
    const totalAiAnalyses = usePeriodValues ? input.periodTotalAiAnalyses : fallbackTotalAiAnalyses;
    const channelsCount = usePeriodValues ? input.periodChannelsCount : 0;
    const productsCount = usePeriodValues ? input.periodProductsCount : input.preparedProducts.length;
    const brandsCount = usePeriodValues ? input.periodBrandsCount : input.preparedBrands.length;
    const companyAverageRating = usePeriodValues ? input.periodAverageRating : fallbackAverageRating;
    const topChannels = usePeriodValues ? input.periodTopChannels : [];
    const topProducts = usePeriodValues ? input.periodTopProducts : fallbackTopProducts;
    const topBrands = usePeriodValues ? input.periodTopBrands : fallbackTopBrands;
    const ratingBuckets = usePeriodValues ? input.periodRatingBuckets : fallbackRatingBuckets;
    const treemapItems = usePeriodValues ? input.periodTreemapItems : fallbackTreemapItems;
    const answersShare = calculateAnswersShare(totalAnswers, totalReviews);

    return {
        totalReviews,
        totalAnswers,
        answersShare,
        totalAiAnalyses,
        channelsCount,
        productsCount,
        brandsCount,
        companyAverageRating,
        topChannels,
        topProducts,
        topBrands,
        ratingBuckets,
        ratingDistributionChartData: buildReportModalRatingDistributionChartData(ratingBuckets),
        averageRatingChartData: buildReportModalAverageRatingChartData(input.averageRatingTimeline),
        responseTimeChartData: buildReportModalResponseTimeChartData(input.averageResponseTimeTimeline),
        treemapItems,
        treemapLayout: buildTreemapLayout(treemapItems),
        aspectRadarPoints: buildReportModalAspectRadarPoints(input.aspectRows),
    };
};
